<template>
    <div>
        <div class="container">
            <SectionIntro
                :title="$t('editor.map.title')"
                class="mt-16"
                :subtitle="$t('editor.map.subtitle')"
                xl
            />
            <MapProductSwitch />
            <div>
                <div class="grid grid-cols-12 mt-4 lg:mt-12 lg:gap-x-8">
                    <div
                        class="col-span-12 lg:col-span-8 h-full box-shadow-preview transition rounded-lg mb-8 sm:mb-8 lg:mb-0"
                    >
                        <div class="lg:sticky top-[var(--header-offset)]">
                            <div
                                class="bg-black bg-opacity-[3%] rounded-tl-lg rounded-tr-lg flex flex-col justify-center relative scale-110 lg:scale-100"
                            >
                                <div>
                                    <div
                                        id="preview-window"
                                        ref="preview-window"
                                        class="relative aspect-1-1 h-auto mx-auto w-auto transition max-h-[90vh] overflow-hidden flex justify-center items-center"
                                    >
                                        <img
                                            class="absolute w-full h-full top-0 left-0 object-contain z-1 preview-shadow"
                                            :src="require('~/assets/images/editors/frames/shadow.webp')"
                                            :alt="$t('editor.preview.shadow_alt')"
                                        />
                                        <ShapeOverlay
                                            :shape="selectedShapeOption.preview"
                                            :color="backgroundColor"
                                            :show-border="selectedOptions.showCutoutFrame"
                                            :border-color="textColor"
                                            class="absolute w-full h-full top-0 left-0 z-30 preview-shape pointer-events-none"
                                        />
                                        <img
                                            v-if="selectedFrame"
                                            :src="require(`~/assets/images/editors/frames/${selectedFrame}`)"
                                            class="absolute w-full h-full top-0 left-0 object-contain z-10 preview-frame pointer-events-none"
                                            :alt="$t('editor.preview.frame_alt')"
                                            data-not-lazy
                                        />
                                        <!-- 54% square centred on CUTOUT_SQUARE so the map fills every shape, including the arch peak -->
                                        <div
                                            id="map"
                                            class="absolute z-20 left-[23.03%] top-[11.41%] w-[54%]"
                                        >
                                            <div class="aspect-w-1 aspect-h-1">
                                                <div>
                                                    <div class="relative h-full">
                                                        <GMap
                                                            ref="gMap"
                                                            language="hu"
                                                            class="min-h-[70%]"
                                                            :center="mapCenter"
                                                            :options="{
                                                                fullscreenControl: false,
                                                                styles: mapStyleRules,
                                                                disableDefaultUI: true,
                                                                keyboardShortcuts: false,
                                                                // mobile: one finger pans, pinch zooms; desktop: plain wheel keeps scrolling the page
                                                                gestureHandling: $device.isMobile ? 'greedy' : 'cooperative',
                                                                // lets the buttons zoom in half steps instead of doubling the scale
                                                                isFractionalZoomEnabled: true,
                                                            }"
                                                            :zoom="zoomLevel"
                                                        >
                                                            <GMapMarker
                                                                v-for="location in locations"
                                                                :key="location.id"
                                                                :position="{ lat: location.lat, lng: location.lng }"
                                                                :options="{
                                                                    icon: require(`~/assets/images/icons/${markerIcon}`),
                                                                }"
                                                            >
                                                            </GMapMarker>
                                                        </GMap>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            ref="previewText"
                                            class="w-[50%] bottom-[13.7%] flex flex-col items-center justify-center absolute z-30 text-center left-1/2 -translate-x-1/2 transition-all pointer-events-none"
                                            :class="selectedShapeOption.textTop"
                                            :style="{ color: textColor }"
                                        >
                                            <span
                                                ref="previewTitle"
                                                class="block preview-title break-words"
                                                :style="titleStyle"
                                                :class="typoClass(selectedOptions.title)"
                                                >{{ selectedOptions.title.value || 'Budapest' }}</span
                                            >
                                            <div
                                                ref="previewMessage"
                                                class="block break-words preview-message"
                                                :style="messageStyle"
                                                :class="typoClass(selectedOptions.message)"
                                                v-html="
                                                    selectedOptions.message.value.replace(/\n/g, '<br>') ||
                                                    $t('editor.message.preview_placeholder')
                                                "
                                            ></div>
                                        </div>
                                        <div
                                            data-html2canvas-ignore
                                            class="absolute bottom-3 right-3 z-40 flex rounded-full overflow-hidden bg-white/95 shadow-md ring-1 ring-black/5 md:hidden"
                                            :title="$t('editor.map.controls')"
                                        >
                                            <button
                                                v-for="control in mapControls"
                                                :key="control.title"
                                                type="button"
                                                class="flex h-9 w-9 items-center justify-center transition active:bg-black/10"
                                                :title="control.title"
                                                :aria-label="control.title"
                                                @click="control.action"
                                            >
                                                <i :class="control.icon" class="text-lg"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <EditorHelp v-if="$device.isDesktop" class="hidden md:flex mb-8 lg:mb-0" />
                        </div>
                    </div>
                    <div class="col-span-12 lg:col-span-4 relative">
                        <div ref="scrollTargetTop"></div>
                        <h2
                            class="font-Inter-Bold text-lg sm:text-xl mb-4 sm:mb-8 text-left border-b border-black border-dashed border-opacity-20 block pb-4"
                        >
                            {{ $t('editor.customize') }}
                        </h2>
                        <EditorFields v-slot="{ compact }">
                            <div class="flex lg:gap-x-4.5 flex-col lg:items-start input-outer-wrapper">
                                <label for="searchTextField" class="text-sm font-Inter-Bold text-grey-900 inline-flex input-label">
                                    {{ $t('editor.location.label') }}
                                </label>
                                <input
                                    id="searchTextField"
                                    ref="locationInput"
                                    v-model="selectedOptions.locationQuery"
                                    type="text"
                                    :placeholder="$t('editor.location.placeholder')"
                                    class="p-inputtext p-component w-full"
                                    @keydown.enter.prevent="selectFirstAutocompletePrediction"
                                />
                            </div>
                            <hr v-if="$device.isDesktop" class="border-b border-black border-dashed border-opacity-10" />
                            <div class="flex lg:gap-x-4.5 flex-col lg:items-start input-outer-wrapper">
                                <span class="text-sm font-Inter-Bold text-grey-900 inline-flex input-label">
                                    {{ $t('editor.style_presets.label') }}
                                </span>
                                <StylePresetSelect
                                    :presets="stylePresets"
                                    :value="activeStylePresetId"
                                    :append-to="compact ? 'body' : null"
                                    @select="applyStylePreset"
                                    @show="onDropdownShow"
                                    @hide="onDropdownHide"
                                />
                            </div>
                            <div class="flex lg:gap-x-4.5 flex-col lg:items-start input-outer-wrapper">
                                <span class="text-sm font-Inter-Bold text-grey-900 inline-flex input-label">
                                    {{ $t('editor.map_style.label') }}
                                </span>
                                <IconSelect
                                    v-model="selectedOptions.style.id"
                                    :options="mapStyleOptions"
                                    :src="(style) => require(`~/assets/images/map_styles/${style.icon}`)"
                                    :placeholder="$t('editor.map_style.placeholder')"
                                    :append-to="compact ? 'body' : null"
                                    @show="onDropdownShow"
                                    @hide="onDropdownHide"
                                />
                            </div>
                            <div class="flex lg:gap-x-4.5 flex-col lg:items-start input-outer-wrapper">
                                <div class="editor-label-row flex justify-between items-center w-full">
                                    <span class="text-sm font-Inter-Bold text-grey-900 inline-flex input-label">
                                        {{ $t('editor.marker.label') }}
                                    </span>
                                    <transition name="slide">
                                        <button
                                            v-if="selectedOptions.marker.id && $device.isDesktop"
                                            type="button"
                                            class="rounded-full flex items-center justify-center p-[0.9rem] pl-3 bg-black text-white transition opacity-80 hover:opacity-100 h-4"
                                            @click="selectedOptions.marker.id = null"
                                        >
                                            <i class="ri-close-line text-base mr-1"></i>
                                            <span class="font-Inter-Semibold text-xs block">{{ $t('editor.marker.hidden') }}</span>
                                        </button>
                                    </transition>
                                </div>
                                <IconSelect
                                    v-model="selectedOptions.marker.id"
                                    :options="markerOptions"
                                    :src="(marker) => require(`~/assets/images/icons/${marker.preview || marker.icon}`)"
                                    :placeholder="$t('editor.marker.placeholder')"
                                    small
                                    :append-to="compact ? 'body' : null"
                                    @show="onDropdownShow"
                                    @hide="onDropdownHide"
                                />
                            </div>
                            <div class="flex lg:gap-x-4.5 flex-col lg:items-start input-outer-wrapper">
                                <span class="text-sm font-Inter-Bold text-grey-900 inline-flex input-label">
                                    {{ $t('editor.shape.label') }}
                                </span>
                                <ShapeSelect
                                    v-model="selectedOptions.shape.id"
                                    :append-to="compact ? 'body' : null"
                                    @show="onDropdownShow"
                                    @hide="onDropdownHide"
                                />
                                <div class="flex items-center justify-between mt-3 w-full">
                                    <label for="showCutoutFrame" class="text-sm font-Inter-Semibold cursor-pointer">{{
                                        $t('editor.shape.cutout_frame')
                                    }}</label>
                                    <InputSwitch
                                        v-model="selectedOptions.showCutoutFrame"
                                        input-id="showCutoutFrame"
                                    />
                                </div>
                            </div>
                            <div class="flex lg:gap-x-4.5 flex-col lg:items-start input-outer-wrapper">
                                <span class="text-sm font-Inter-Bold text-grey-900 inline-flex input-label">
                                    {{ $t('editor.background.label') }}
                                </span>
                                <ColorSwatches
                                    :options="backgroundOptions"
                                    :selected-id="selectedOptions.background.id"
                                    :color="backgroundColor"
                                    :placeholder="$t('editor.background.placeholder')"
                                    @select="selectedOptions.background = $event"
                                    @show="onDropdownShow"
                                    @hide="onDropdownHide"
                                />
                            </div>
                            <div class="flex lg:gap-x-4.5 flex-col lg:items-start input-outer-wrapper">
                                <span class="text-sm font-Inter-Bold text-grey-900 inline-flex input-label">
                                    {{ $t('editor.text_color.label') }}
                                </span>
                                <ColorSwatches
                                    :options="textColorOptions"
                                    :selected-id="selectedOptions.textColor ? selectedOptions.textColor.id : 0"
                                    :color="textColor"
                                    :placeholder="$t('editor.text_color.placeholder')"
                                    @select="selectedOptions.textColor = $event"
                                    @show="onDropdownShow"
                                    @hide="onDropdownHide"
                                />
                            </div>
                            <div class="input-outer-wrapper">
                                <Input
                                    v-model="selectedOptions.size.id"
                                    class="w-full"
                                    :placeholder="$t('editor.size.placeholder')"
                                    :options="sizeOptions"
                                    is-dropdown
                                    :label="$t('editor.size.label')"
                                    :show="onDropdownShow"
                                    :hide="onDropdownHide"
                                />
                            </div>
                            <hr v-if="$device.isDesktop" class="border-b border-black border-dashed border-opacity-10" />
                            <div class="w-full input-outer-wrapper">
                                <div class="editor-label-row flex justify-between items-center">
                                    <span class="text-sm font-Inter-Bold text-grey-900 inline-flex input-label">
                                        {{ $t('editor.title.label') }}
                                    </span>
                                    <TextSizeToggle
                                        :value="selectedOptions.title.size || 1"
                                        :label="$t('editor.title.size')"
                                        @input="setTextSize('title', $event)"
                                    />
                                </div>
                                <Input
                                    v-model="selectedOptions.title.value"
                                    class="w-full"
                                    :placeholder="$t('editor.title.placeholder')"
                                    :required="false"
                                    :maxlength="40"
                                />
                            </div>
                            <FontFamilySelector
                                :model-value="selectedOptions.title.typo"
                                :label="$t('editor.title.font')"
                                :show-tooltip="$device.isDesktop"
                                wrapper-class="title-typo flex flex-col items-start"
                                value-text-class="text-xl"
                                option-text-class="text-xl"
                                @update:modelValue="setTypo('title', $event)"
                                @show="onDropdownShow"
                                @hide="onDropdownHide"
                            />
                            <hr v-if="$device.isDesktop" class="border-b border-black border-dashed border-opacity-10" />
                            <div class="w-full input-outer-wrapper">
                                <div class="editor-label-row flex justify-between items-center">
                                    <span class="text-sm font-Inter-Bold text-grey-900 inline-flex input-label">
                                        {{ $t('editor.message.label') }}
                                    </span>
                                    <TextSizeToggle
                                        :value="selectedOptions.message.size || DEFAULT_MESSAGE_SIZE"
                                        :label="$t('editor.message.size')"
                                        @input="setTextSize('message', $event)"
                                    />
                                </div>
                                <Input
                                    v-model="selectedOptions.message.value"
                                    class="w-full"
                                    :placeholder="$t('editor.message.placeholder')"
                                    :required="false"
                                    :maxlength="180"
                                    :rows="3"
                                    :is-textarea="$device.isDesktop"
                                />
                            </div>
                            <FontFamilySelector
                                :model-value="selectedOptions.message.typo"
                                :label="$t('editor.message.font')"
                                :show-tooltip="$device.isDesktop"
                                wrapper-class="message-typo w-full flex flex-col items-start"
                                value-text-class="text-xl"
                                option-text-class="text-xl"
                                @update:modelValue="setTypo('message', $event)"
                                @show="onDropdownShow"
                                @hide="onDropdownHide"
                            />
                            <hr v-if="$device.isDesktop" class="border-b border-black border-dashed border-opacity-10" />
                            <CardPicker
                                v-model="selectedOptions.frame.id"
                                name="frame"
                                :label="$t('editor.frame.label')"
                                :options="frameOptions"
                                :src="(frame) => require(`~/assets/images/dev/editor/${frame.icon}`)"
                                :placeholder="$t('editor.frame.placeholder')"
                                :clear-label="$t('editor.frame.clear')"
                                :append-to="compact ? 'body' : null"
                                @clear="clearOption('frame')"
                                @show="onDropdownShow"
                                @hide="onDropdownHide"
                            />
                            <hr v-if="$device.isDesktop" class="border-b border-black border-dashed border-opacity-10" />
                        </EditorFields>
                        <div class="w-ful mt-4 mb-6">
                            <Input
                                v-model="selectedOptions.hasDigital.active"
                                is-checkbox
                                :label="`<span class='font-Inter-Bold text-base'> ${$t('editor.digital.label')}</span> <span class='font-Inter-Regular text-base'>(+ 500 Ft.)</span>`"
                                :required="false"
                                class="font-Inter-Bold"
                            />
                        </div>
                        <div class="w-full lg:mb-4">
                            <Input
                                v-model="selectedOptions.comment"
                                is-textarea
                                :label="$t('editor.comment.label')"
                                :rows="3"
                                :placeholder="$t('editor.comment.placeholder')"
                                :required="false"
                            />
                        </div>
                        <EditorHelp v-if="$device.isMobile" mobile class="lg:mb-4" />
                        <StickyPriceCard
                            :quantity="quantity"
                            name="Egyedi csillagtérkép"
                            :price="price"
                            :image="previewImage"
                            :before-add-to-cart="capturePreview"
                            :slug="
                                localePath({
                                    route: 'editors-map',
                                    hash: `#${randomId}`,
                                })
                            "
                            :product-id="125"
                            :options="mutatedOptions"
                            :is-preview-capturing="isPreviewCapturing"
                            :is-dropdown-open="isDropdownOpen"
                            :scroll-target-bottom="$refs.scrollTargetBottom"
                            :scroll-target-top="$refs.scrollTargetTop"
                            :extras="cartExtras"
                        />
                        <div ref="scrollTargetBottom"></div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="!$cookies.get('is-map-editor-instructions-seen')">
            <EditorInstructionsModal
                cookie="is-map-editor-instructions-seen"
                :start-index="instructionSteps.length - 1"
                :steps="instructionSteps"
                carousel-id="map-instructions-carousel"
            />
        </div>
    </div>
</template>
<script>
import mapStyles from '@/components/UI/Base/MapStyles';
import StickyPriceCard from '@/components/Cards/StickyPriceCard/StickyPriceCard.vue';
import EditorInstructionsModal from '@/components/Modals/EditorInstructionsModal.vue';
import FontFamilySelector from '@/components/UI/FontFamilySelector/FontFamilySelector.vue';
import { getFontMetrics, getTypoScale, trimmedTextStyle } from '@/components/UI/FontFamilySelector/fontMetrics';
import ShapeOverlay from '@/components/Editor/Map/ShapeOverlay.vue';
import CardPicker from '@/components/Editor/Shared/CardPicker.vue';
import ColorSwatches from '@/components/Editor/Shared/ColorSwatches.vue';
import EditorFields from '@/components/Editor/Shared/EditorFields.vue';
import EditorHelp from '@/components/Editor/Shared/EditorHelp.vue';
import MapProductSwitch from '@/components/Editor/Shared/MapProductSwitch.vue';
import IconSelect from '@/components/Editor/Shared/IconSelect.vue';
import ShapeSelect from '@/components/Editor/Shared/ShapeSelect.vue';
import StylePresetSelect from '@/components/Editor/Shared/StylePresetSelect.vue';
import TextSizeToggle from '@/components/Editor/Shared/TextSizeToggle.vue';
import editorMixin from '@/components/Editor/Shared/editorMixin';
import {
    DEFAULT_MESSAGE_SIZE,
    DEFAULT_MESSAGE_TYPO,
    DEFAULT_TITLE_TYPO,
} from '@/components/Editor/Shared/productOptions';
import { textOverflows, FIT_STEP, MIN_FIT } from '@/components/Editor/Shared/textFit';
import { SHAPES, shapeById } from '@/components/Editor/Shared/shapes';
import { COLORS, contrastColor, POSTER_BACKGROUNDS, TEXT_COLORS } from '@/components/Editor/Shared/designTokens';
import { MAP_DETAIL_RULES, MAP_STYLES, MARKERS } from '@/components/Editor/Map/options';
import { buildStylePresets, presetMatches } from '@/components/Editor/Map/stylePresets';

// Preview typography, relative to the preview width (font sizes, gap) or to the cap height of
// the chosen font (line heights), so every font gets the same rhythm as the default Figtree.
const TITLE_SIZE = 1 / 17;
const MESSAGE_SIZE = 1 / 36;
const MESSAGE_SIZE_MOBILE = 1 / 30;
const TITLE_MESSAGE_GAP = 0.0364 * 0.56;
// descender depth (in em) the gap is already tuned for; deeper title descenders add to it
const TYPICAL_DESCENDER = 0.22;
const TITLE_LINE_HEIGHT = 1.78;
const MESSAGE_LINE_HEIGHT = 1.57;
const MAX_PLACE_ZOOM = 15;

export default {
    name: 'MapEditor',
    components: {
        StickyPriceCard,
        EditorInstructionsModal,
        FontFamilySelector,
        ShapeOverlay,
        CardPicker,
        ColorSwatches,
        EditorFields,
        EditorHelp,
        IconSelect,
        MapProductSwitch,
        ShapeSelect,
        StylePresetSelect,
        TextSizeToggle,
    },
    mixins: [editorMixin],
    data() {
        return {
            DEFAULT_MESSAGE_SIZE,
            basePrice: 11000,
            editorType: 'map',
            mapStyles,
            mapStyleOptions: MAP_STYLES,
            markerOptions: MARKERS,
            backgroundOptions: POSTER_BACKGROUNDS,
            textColorOptions: TEXT_COLORS,
            mapCenter: { lat: 47.4979, lng: 19.0402 },
            zoomLevel: 10,
            locations: [{ id: 1, lat: 47.4979, lng: 19.0402 }],
            previewWidth: 0,
            fontMetrics: { title: null, message: null },
            // shrink factor applied on top of the chosen text sizes so the texts stay inside their box
            textFit: 1,
            selectedOptions: {
                size: { id: 1, name: 'A5', price: 0 },
                style: { id: 1, name: 'Fekete/Fehér', label: 'black', icon: 'black.svg' },
                marker: { id: null, name: null, preview: null, price: 0 },
                shape: { id: 1, name: 'rectangle', price: 0 },
                showCutoutFrame: true,
                frame: { id: 2, name: 'Fehér', preview: 'frame_2.png', price: 2000 },
                title: { value: '', size: 1, typo: { ...DEFAULT_TITLE_TYPO } },
                message: { value: '', size: DEFAULT_MESSAGE_SIZE, typo: { ...DEFAULT_MESSAGE_TYPO } },
                background: { id: 1, name: 'Fehér', color: COLORS.paper },
                textColor: { id: 0, name: 'Automatikus (háttérhez igazodik)', color: null },
                comment: null,
                preview: null,
                hasDigital: { active: false, price: 500 },
                locationLatLong: '',
                zoomLevel: '',
                locationQuery: '',
            },
        };
    },
    computed: {
        mapStyleRules() {
            return [...mapStyles[this.selectedMapStyle.label], ...MAP_DETAIL_RULES];
        },
        mapControls() {
            return [
                { title: this.$t('editor.map.zoom_out'), icon: 'ri-subtract-line', action: () => this.zoomBy(-0.5) },
                { title: this.$t('editor.map.zoom_in'), icon: 'ri-add-line', action: () => this.zoomBy(0.5) },
            ];
        },
        instructionSteps() {
            return [1, 2, 3, 4, 5, 6].map((id) => ({
                id,
                title: this.$t(`editor.map.instructions.step${id}_title`),
                description: this.$t(`editor.map.instructions.step${id}_description`),
                illustration: `map-step_${id}.webp`,
            }));
        },
        selectedMapStyle() {
            return MAP_STYLES.find((style) => style.id === this.selectedOptions.style?.id) || MAP_STYLES[0];
        },
        selectedShapeOption() {
            return shapeById(this.selectedOptions.shape.id);
        },
        titleStyle() {
            const style = this.previewTextStyle('title', TITLE_SIZE, TITLE_LINE_HEIGHT, 0);
            const metrics = this.fontMetrics.title;
            if (!metrics || !style.marginBottom) return style;
            const extra = Math.max(0, metrics.descender - TYPICAL_DESCENDER) * parseFloat(style.fontSize);
            return { ...style, marginBottom: `${parseFloat(style.marginBottom) + extra}px` };
        },
        messageStyle() {
            const size = this.$device.isMobile ? MESSAGE_SIZE_MOBILE : MESSAGE_SIZE;
            const style = this.previewTextStyle('message', size, MESSAGE_LINE_HEIGHT, TITLE_MESSAGE_GAP);
            const extra = this.previewWidth * 0.03 * 0.5 * 0.56;
            const current = parseFloat(style.marginTop) || 0;
            return extra ? { ...style, marginTop: `${current + extra}px` } : style;
        },
        backgroundColor() {
            const { id, color } = this.selectedOptions.background || {};
            return color || (POSTER_BACKGROUNDS.find((option) => option.id === id) || POSTER_BACKGROUNDS[0]).color;
        },
        textColor() {
            return this.selectedOptions.textColor?.color || contrastColor(this.backgroundColor);
        },
        stylePresets() {
            return buildStylePresets(POSTER_BACKGROUNDS, TEXT_COLORS, MAP_STYLES, SHAPES);
        },
        activeStylePresetId() {
            const active = this.stylePresets.find((preset) =>
                presetMatches(
                    preset,
                    this.backgroundColor,
                    this.textColor,
                    this.selectedOptions.style?.id,
                    this.selectedOptions.title?.typo?.label,
                    this.selectedOptions.message?.typo?.label,
                    this.selectedOptions.shape?.id
                )
            );
            return active ? active.id : null;
        },
        markerIcon() {
            const option = MARKERS.find((item) => item.id === this.selectedOptions.marker?.id);
            return option?.preview || option?.icon || 'marker--invisible.svg';
        },
    },
    watch: {
        selectedOptions: {
            handler() {
                this.debouncedCheckOptions(this.selectedOptions);
                this.fitText();
            },
            deep: true,
        },
        'selectedOptions.title.typo': 'updateFontMetrics',
        'selectedOptions.message.typo': 'updateFontMetrics',
        'selectedMapStyle.label': 'changeMapStyle',
        mapCenter(newCenter) {
            if (this.$refs.gMap) {
                this.$refs.gMap.map.setCenter(newCenter);
            }
            this.$nextTick(() => {
                this.$refs.gMap.markers.forEach((marker) => {
                    marker.setPosition(newCenter);
                });
            });
        },
        markerIcon(icon) {
            if (!this.selectedOptions.marker?.id || !this.$refs.gMap?.markers) return;
            this.$refs.gMap.markers.forEach((marker) => {
                if (marker) marker.setIcon(require(`~/assets/images/icons/${icon}`));
            });
        },
    },
    async mounted() {
        if (this.$device.isSafari) {
            document.body.style.overscrollBehavior = 'auto';
            window.addEventListener('wheel', this.handleWheel);
        }
        window.addEventListener('resize', this.updateFontSize);
        this.updateFontSize();
        this.updateFontMetrics();
        await this.initializeGoogleMaps();

        const saved = this.loadSavedProduct();
        if (saved) {
            this.selectedOptions = saved;
            this.changeMapStyle();

            if (this.selectedOptions.locationLatLong) {
                const [lat, lng] = this.selectedOptions.locationLatLong
                    .split(',')
                    .map((coord) => parseFloat(coord.trim()));

                if (!isNaN(lat) && !isNaN(lng)) {
                    this.setMapLocation({ lat, lng });
                } else {
                    console.error('Invalid latitude or longitude values:', this.selectedOptions.locationLatLong);
                }
            }
            if (this.selectedOptions.zoomLevel) {
                this.$refs.gMap.map.setZoom(this.selectedOptions.zoomLevel);
            }
        }
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateFontSize);
        if (this.$device.isSafari) {
            window.location.reload();
            window.removeEventListener('wheel', this.handleWheel);
        }
    },
    methods: {
        typoClass(part) {
            return part?.typo?.class || part?.typo || '';
        },
        changeMapStyle() {
            this.$refs.gMap?.map?.setOptions({ styles: this.mapStyleRules });
        },
        updateFontSize() {
            this.$nextTick(() => {
                const previewWindow = this.$refs['preview-window'];
                if (!previewWindow || !previewWindow.clientWidth) return;
                this.previewWidth = previewWindow.clientWidth;
                this.fitText();
            });
        },
        async updateFontMetrics() {
            await this.$nextTick();
            const { previewTitle, previewMessage } = this.$refs;
            if (!previewTitle || !previewMessage) return;

            const request = (this.fontMetricsRequest = (this.fontMetricsRequest || 0) + 1);
            const [title, message] = await Promise.all([getFontMetrics(previewTitle), getFontMetrics(previewMessage)]);
            if (request === this.fontMetricsRequest) {
                this.fontMetrics = { title, message };
                this.fitText();
            }
        },
        // Re-renders with a smaller textFit until the title and message fit their box.
        async fitText() {
            const request = (this.fitTextRequest = (this.fitTextRequest || 0) + 1);
            this.textFit = 1;
            await this.$nextTick();
            const { previewText, previewTitle, previewMessage } = this.$refs;
            if (!previewText) return;
            while (
                request === this.fitTextRequest &&
                this.textFit > MIN_FIT &&
                textOverflows(previewText, [previewTitle, previewMessage])
            ) {
                this.textFit -= FIT_STEP;
                await this.$nextTick();
            }
        },
        previewTextStyle(part, sizeRatio, capLineHeight, gapRatio) {
            if (!this.previewWidth) return {};

            const scale = getTypoScale(this.selectedOptions[part]?.typo);
            const fontSize =
                this.previewWidth * sizeRatio * scale * (this.selectedOptions[part]?.size || 1) * this.textFit;
            const metrics = this.fontMetrics[part];
            return {
                // the per-font scale narrows the text column as well as the glyphs
                maxWidth: `${Math.min(scale, 1) * 100}%`,
                ...(metrics
                    ? trimmedTextStyle(metrics, fontSize, capLineHeight, this.previewWidth * gapRatio)
                    : { fontSize: `${fontSize}px` }),
            };
        },
        applyStylePreset(preset) {
            if (!preset || !preset.options) return;
            const { background, text, mapStyle, shape, titleTypo, messageTypo } = preset.options;
            this.selectedOptions = JSON.parse(
                JSON.stringify({
                    ...this.selectedOptions,
                    background,
                    textColor: text,
                    ...(mapStyle ? { style: mapStyle } : {}),
                    ...(shape ? { shape } : {}),
                    title: { ...this.selectedOptions.title, typo: titleTypo || this.selectedOptions.title.typo },
                    message: {
                        ...this.selectedOptions.message,
                        typo: messageTypo || this.selectedOptions.message.typo,
                    },
                })
            );
        },
        handleWheel(event) {
            window.scrollBy({ top: event.deltaY });
            event.preventDefault();
        },
        zoomBy(delta) {
            const map = this.$refs.gMap?.map;
            if (!map) return;
            const nextZoom = Math.min(Math.max(map.getZoom() + delta, 1), 21);
            map.setZoom(nextZoom);
            this.selectedOptions.zoomLevel = nextZoom;
            this.zoomLevel = nextZoom;
        },
        async initializeGoogleMaps() {
            this.google = await this.$GMaps.google;
            if (!this.google) return;
            await this.google.maps.importLibrary('places');
            const autocomplete = new this.google.maps.places.Autocomplete(this.$refs.locationInput);
            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                if (place.geometry) this.updateMapLocation(place);
                else console.warn('No details available for input:', place.name);
            });

            const map = this.$refs.gMap.map;
            map.addListener('dragend', () => {
                const center = map.getCenter();
                this.selectedOptions.locationLatLong = `${center.lat()}, ${center.lng()}`;
            });
            map.addListener('zoom_changed', () => {
                setTimeout(() => {
                    this.selectedOptions.zoomLevel = map.getZoom();
                }, 250);
            });
        },
        async setMapLocation(location) {
            if (location.lat && location.lng) {
                const latLng = new this.google.maps.LatLng(location.lat, location.lng);
                await this.$refs.gMap.map.setCenter(latLng);
                this.$refs.gMap.map.setZoom(this.selectedOptions.zoomLevel || 12);
                this.mapCenter = latLng;
            } else {
                console.error('Invalid location data:', location);
            }
        },
        updateMapLocation(place) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            this.mapCenter = { lat, lng };
            this.locations = [{ id: Date.now(), lat, lng }];
            this.$refs.locationInput.blur(); // hides the keyboard and lets the preview scroll into view

            this.selectedOptions.locationQuery = place.name;
            this.selectedOptions.locationLatLong = `${lat}, ${lng}`;
            this.selectedOptions.title.value = place.name;

            const map = this.$refs.gMap.map;
            map.setCenter(this.mapCenter);

            if (place.geometry.viewport) {
                map.fitBounds(place.geometry.viewport);
                map.setZoom(this.calculateZoomLevel(place.geometry.viewport));
            } else {
                const bounds = new this.google.maps.LatLngBounds();
                bounds.extend(new this.google.maps.LatLng(lat, lng));
                map.fitBounds(bounds);
            }

            // added once: every search used to stack another listener
            if (!this.maxZoomListener) {
                this.maxZoomListener = map.addListener('zoom_changed', () => {
                    if (map.getZoom() > MAX_PLACE_ZOOM) map.setZoom(MAX_PLACE_ZOOM);
                    this.selectedOptions.zoomLevel = map.getZoom();
                });
            }
        },
        calculateZoomLevel(bounds) {
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const zoomFromSize = (diff) => Math.max(0, Math.min(20, Math.log(1 / (diff * 0.01)) / Math.LN2));
            return Math.min(zoomFromSize(ne.lat() - sw.lat()), zoomFromSize(ne.lng() - sw.lng()));
        },
        selectFirstAutocompletePrediction() {
            if (!this.google) return;
            const { places } = this.google.maps;
            new places.AutocompleteService().getPlacePredictions(
                { input: this.selectedOptions.locationQuery },
                (predictions, status) => {
                    if (status !== places.PlacesServiceStatus.OK || !predictions.length) return;
                    new places.PlacesService(this.$refs.gMap.map).getDetails(
                        { placeId: predictions[0].place_id },
                        (place, detailStatus) => {
                            if (detailStatus === places.PlacesServiceStatus.OK && place.geometry) {
                                this.updateMapLocation(place);
                            }
                        }
                    );
                }
            );
        },
    },
};
</script>

<style>
.GMap,
.GMap__Wrapper {
    height: 100% !important;
}

/* Google chrome (logo, Térképadatok/Feltételek, keyboard) sits on the poster artwork */
#map .gm-style-cc,
#map .gmnoprint,
#map .gmnoprint ~ div,
#map .gm-style a[href*='maps.google.com/maps'],
#map .gm-style a[href*='terms_maps'],
#map .gm-style img[alt='Google'],
#map .gm-style [title*='Google Térképen'],
#map .gm-style [aria-label*='Google Maps'],
#map .gm-style [aria-label*='Google Térkép'],
#map .gm-style [aria-label*='Keyboard'],
#map .gm-style [aria-label*='Billentyű'] {
    display: none !important;
}

/* desktop column: keep open panels above the sticky price card */
.editor-fields .p-dropdown-panel {
    z-index: 9999 !important;
}
</style>
